import { format } from 'date-fns';
import { pathToUrl } from '@domain/routes/urls';

export interface Regulations {
  id: number;
  aggregate_id: null;
  gear_id: number;
  location_id: number;
  measurement_id: number;
  parent_id: number;
  specie_shape_id: number;
  fish_id: number;
  aggregate_limit: null | number;
  bag_limit: number | null;
  vessel_limit: number | null;
  trophy_limit: number | null;
  min_size: number | null;
  max_size: number | null;
  trophy_size: number | null;
  location_rank: number;
  prohibited: number;
  freshwater: number;
  saltwater: number;
  species_report_only: number;
  published: number;
  effective: number;
  location_prohibited: number;
  location_freshwater: number;
  location_saltwater: number;
  location_is_top_level: number;
  production: number;
  additional_licenses_required: string;
  aggregate_name: null;
  gear_name: string;
  location_area: string;
  location_name: string;
  location_prohibited_message: string;
  measurement_name: string;
  measurement_abbreviation: string;
  measurement_unit: string;
  measurement_unit_symbol: string;
  species: string;
  shape: string;
  notes: string;
  location_regulation_notes: string;
  gear_description: string;
  measurement_description: string;
  edibility: string;
  synonyms: string[];
  seasons: {
    starts_at: {
      date: string;
      timezone_type: number;
      timezone: string;
    };
    ends_at: {
      date: string;
      timezone_type: number;
      timezone: string;
    };
    repeat: boolean;
    description: string;
  }[];
  gigging: number;
  snagging: number;
  spearing: number;
  multiple_hooks: number;
  zero_bag_for_captain: number;
  keep_intact: number;
  effective_at: string;
  prohibitions: {
    id: number;
    label: string;
    name: string;
    description: string;
    type: string;
  }[];
  restrictions: {
    id: number;
    label: string;
    name: string;
    description: string;
    type: string;
  }[];
  requirements: {
    id: number;
    label: string;
    name: string;
    description: string;
    type: string;
  }[];
}

interface SpeciesImage {
  url: string;
  width: number;
  height: number;
}

export interface SpeciesRegulations {
  displayName: string;
  image?: SpeciesImage;
  regulations: Regulations;
  fetchedAt: string;
}

interface SpeciesInput {
  fishrulesSpeciesIds: number[];
  displayName?: string;
  image?: SpeciesImage;
}

interface Variables {
  latitude: number;
  longitude: number;
  species: SpeciesInput[];
  signal?: AbortSignal;
}

interface FetchResult {
  success: boolean;
  results: Regulations[];
  failed?: number[];
}

async function fetchSpeciesRegulationsAtLocationBulk(
  fishrulesSpeciesIds: number[],
  latitude: number,
  longitude: number,
  signal: AbortSignal,
): Promise<FetchResult> {
  const response = await fetch(
    pathToUrl('/api/_fishrules/get-species-regulations-at-location-bulk'),
    {
      body: JSON.stringify({ latitude, longitude, fishrulesSpeciesIds }),
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    },
  );
  const json: FetchResult = await response.json();
  return json;
}

// The FishRules API may return multiple regulation entries per species,
// but we only use the first one.
export async function getSpeciesRegulationsAtLocation({
  latitude,
  longitude,
  species,
  signal = new AbortController().signal,
}: Variables): Promise<SpeciesRegulations[]> {
  if (species.length === 0) {
    return [];
  }

  const fetchedAt = format(new Date(), 'MMMM do, yyyy');
  const allFishrulesIds = species.flatMap(s => s.fishrulesSpeciesIds);
  const { results } = await fetchSpeciesRegulationsAtLocationBulk(
    allFishrulesIds,
    latitude,
    longitude,
    signal,
  );

  // Rutilus may return multiple fishrules IDs per species (regional variants).
  // We send all candidates and accept the first result whose fish_id matches
  // any of them.
  return species
    .map(({ fishrulesSpeciesIds, displayName, image }) => {
      const regulations = results.find(r => fishrulesSpeciesIds.includes(r.fish_id));
      if (!regulations) {
        return undefined;
      }
      return {
        displayName: displayName ?? regulations.species,
        ...(image && { image }),
        regulations,
        fetchedAt,
      };
    })
    .filter((entry): entry is SpeciesRegulations => entry !== undefined);
}
